import { ReactNode, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSidebarExtra } from '../../context/SidebarExtraContext';
import { useToggles } from '../../context/TogglesContext';
import { useCsvActions } from '../../context/CsvActionsContext';
import { useAccent } from '../../context/AccentContext';
import { useT } from '../../i18n';
import { ProfileMenu } from './ProfileMenu';
import { Moon, Sun, ChevronLeft, ChevronDown, ChevronRight } from 'lucide-react';

type Page = 'dashboard' | 'resources' | 'capabilities' | 'outcomes' | 'inventory-resources' | 'inventory-capabilities' | 'inventory-outcomes' | 'linking' | 'link-qualification' | 'maturity-scoring' | 'weight-scoring' | 'maturity-qualification' | 'vrio' | 'gap-analysis' | 'strategic-portfolio' | 'admin';

type RCPage = 'linking' | 'link-qualification' | 'maturity-scoring' | 'weight-scoring' | 'maturity-qualification' | 'vrio';

interface AppShellProps {
  children: ReactNode;
  currentPage: Page;
  onNavigate: (page: Page) => void;
}

const INVENTORY_PAGES: Page[] = ['inventory-resources', 'inventory-capabilities', 'inventory-outcomes'];
const RC_ASSESSMENT_PAGES: Page[] = ['linking', 'link-qualification', 'maturity-scoring', 'weight-scoring', 'maturity-qualification', 'vrio'];
const RC_INSIGHTS_PAGES: Page[] = ['gap-analysis', 'strategic-portfolio'];

export function AppShell({ children, currentPage, onNavigate }: AppShellProps) {
  const { user, logout, isAdmin, impersonatedBy, stopImpersonating } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { sidebarExtra } = useSidebarExtra();
  const { isTabAccessible, isTabUnlockedForUsers } = useToggles();
  const { csvActions } = useCsvActions();
  const { logoUrl } = useAccent();
  const { t } = useT();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem('sidebarCollapsed');
    return stored === 'true';
  });
  const [inventoryDropdownOpen, setInventoryDropdownOpen] = useState(false);
  const [rcDropdownOpen, setRcDropdownOpen] = useState(false);
  const [rcInsightsDropdownOpen, setRcInsightsDropdownOpen] = useState(false);

  useEffect(() => {
    if (INVENTORY_PAGES.includes(currentPage)) {
      setInventoryDropdownOpen(true);
    }
    if (RC_ASSESSMENT_PAGES.includes(currentPage)) {
      setRcDropdownOpen(true);
    }
    if (RC_INSIGHTS_PAGES.includes(currentPage)) {
      setRcInsightsDropdownOpen(true);
    }
  }, [currentPage]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const navItem = (page: Page, label: string, num?: number) => {
    const isRCPage = RC_ASSESSMENT_PAGES.includes(page);
    const accessible = isRCPage ? isTabAccessible(page as RCPage) : true;
    const userLocked = isRCPage && !isTabUnlockedForUsers(page as RCPage);

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (!accessible) return;
      onNavigate(page);
    };

    const className = [
      'nav-link',
      currentPage === page ? 'active' : '',
      // Regular users: fully locked (greyed out + not clickable)
      !accessible ? 'nav-link--locked' : '',
      // Admins: greyed out appearance but still clickable
      accessible && userLocked ? 'nav-link--dimmed' : '',
    ].filter(Boolean).join(' ');

    return (
      <a
        href={`#${page}`}
        className={className}
        onClick={handleClick}
      >
        {num != null ? (
          <span className="nav-link-numbered">
            <span className="nav-link-num">{num}</span>
            <span>{label}</span>
          </span>
        ) : (
          label
        )}
      </a>
    );
  };

  return (
    <>
      {/* ── Topbar ── */}
      <header className="topbar">
        <div className="topbar-left">
          <div className="brand">
            {logoUrl && (
              <img src={logoUrl} alt="" className="brand-logo" onClick={() => onNavigate('dashboard')} />
            )}
            <span className="brand-mark" onClick={() => onNavigate('dashboard')} style={{ cursor: 'pointer' }}>RCAJO</span>
            <span className="brand-sep" />
            <span className="brand-page">{t(`page.${currentPage}`)}</span>
          </div>
          <div id="header-actions-portal"></div>
        </div>
        <div className="topbar-right">
          {csvActions && (
            <>
              {csvActions.onUpload && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 'var(--t-11)', padding: '2px 10px' }}
                  onClick={csvActions.onUpload}
                  disabled={csvActions.uploading}
                >
                  {csvActions.uploading ? t('csv.importing') : (csvActions.uploadLabel || t('csv.uploadCsv'))}
                </button>
              )}
              {csvActions.downloadUrl && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 'var(--t-11)', padding: '2px 10px' }}
                  onClick={() => window.open(csvActions.downloadUrl, '_blank')}
                >
                  {csvActions.downloadLabel || t('csv.exportCsv')}
                </button>
              )}
              {csvActions.secondaryDownloadUrl && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 'var(--t-11)', padding: '2px 10px' }}
                  onClick={() => window.open(csvActions.secondaryDownloadUrl, '_blank')}
                >
                  {csvActions.secondaryDownloadLabel || t('csv.export')}
                </button>
              )}
            </>
          )}
          <button className="theme-btn" onClick={toggleTheme} title={theme === 'light' ? t('common.theme.switchToDark') : t('common.theme.switchToLight')}>
            {theme === 'light' ? <Moon className="icon-sm" /> : <Sun className="icon-sm" />}
          </button>
          <ProfileMenu />
          {isAdmin && (
            <span
              className="badge badge-accent badge-clickable"
              onClick={() => onNavigate('admin')}
              title={t('page.admin')}
            >
              {t('admin.badge')}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={logout}>
            {t('common.logout')}
          </button>
        </div>
      </header>

      {/* ── Shell ── */}
      <div className={`shell ${sidebarCollapsed ? 'shell--collapsed' : ''}`}>
        <aside className="sidebar">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? <ChevronRight className="icon-sm" /> : <><ChevronLeft className="icon-sm" /> {t('nav.collapse')}</>}
          </button>

          {!sidebarCollapsed && (
            <nav className="sidebar-nav">
              <div className="nav-section">
                <div
                  className={`nav-section-title nav-section-title-clickable ${inventoryDropdownOpen ? 'open' : ''}`}
                  onClick={() => setInventoryDropdownOpen(!inventoryDropdownOpen)}
                >
                  <span>{t('nav.inventory')}</span>
                  <span className="nav-dropdown-arrow">{inventoryDropdownOpen ? <ChevronDown className="icon-sm" /> : <ChevronRight className="icon-sm" />}</span>
                </div>
                {inventoryDropdownOpen && (
                  <div className="nav-dropdown-items">
                    {navItem('inventory-resources', t('nav.item.resources'))}
                    {navItem('inventory-capabilities', t('nav.item.capabilities'))}
                    {navItem('inventory-outcomes', t('nav.item.outcomes'))}
                  </div>
                )}
              </div>
              <div className="nav-section">
                <div
                  className={`nav-section-title nav-section-title-clickable ${rcDropdownOpen ? 'open' : ''}`}
                  onClick={() => setRcDropdownOpen(!rcDropdownOpen)}
                >
                  <span>{t('nav.rcAssessment')}</span>
                  <span className="nav-dropdown-arrow">{rcDropdownOpen ? <ChevronDown className="icon-sm" /> : <ChevronRight className="icon-sm" />}</span>
                </div>
                {rcDropdownOpen && (
                  <div className="nav-dropdown-items">
                    {navItem('linking', t('nav.item.linking'), 1)}
                    {navItem('link-qualification', t('nav.item.linkQualification'), 2)}
                    {navItem('maturity-scoring', t('nav.item.maturityScoring'), 3)}
                    {navItem('weight-scoring', t('nav.item.weightScoring'), 4)}
                    {navItem('maturity-qualification', t('nav.item.weightMaturityQualification'), 5)}
                    {navItem('vrio', t('nav.item.vrio'), 6)}
                  </div>
                )}
              </div>
              <div className="nav-section">
                <div
                  className={`nav-section-title nav-section-title-clickable ${rcInsightsDropdownOpen ? 'open' : ''}`}
                  onClick={() => setRcInsightsDropdownOpen(!rcInsightsDropdownOpen)}
                >
                  <span>{t('nav.rcInsights')}</span>
                  <span className="nav-dropdown-arrow">{rcInsightsDropdownOpen ? <ChevronDown className="icon-sm" /> : <ChevronRight className="icon-sm" />}</span>
                </div>
                {rcInsightsDropdownOpen && (
                  <div className="nav-dropdown-items">
                    {navItem('gap-analysis', t('nav.item.gapAnalysis'))}
                    {navItem('strategic-portfolio', t('nav.item.strategicPortfolio'))}
                  </div>
                )}
              </div>
            </nav>
          )}

          {!sidebarCollapsed && sidebarExtra && (
            <div className="sidebar-extra">
              {sidebarExtra}
            </div>
          )}
        </aside>

        <main className="content">
          {impersonatedBy && (
            <div className="impersonation-banner">
              <span>{t('impersonation.viewingAs')} <strong>{user?.name}</strong> — {t('impersonation.by')} {impersonatedBy.name}</span>
              <button className="btn btn-sm" onClick={() => { stopImpersonating(); window.location.reload(); }}>
                {t('impersonation.stop')}
              </button>
            </div>
          )}
          <div className="page">{children}</div>
        </main>
      </div>
    </>
  );
}
